import useLegacyExternalQuota from '@/hooks/useLegacyExternalQuota';
import LegacyExternalQuotaNotice from '@/components/DmpV2/LegacyExternalQuotaNotice';
import { isExternalReconciliation, isExternalOutcomeUncertain, isLegacyBalanceReconciliation } from '@/utils/legacyExternalQuota';
import { legacyExternalRequestKeys } from '@/utils/legacyExternalRequestKey';
import { newRequestKey } from '@/components/DmpV2/purchaseIntent';
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Wrapper } from '../../styles';
import { Box, Flex, LoadingPage } from '@/layout';
import { InputWrapper } from '@/components/Container/AuthContainer/styles';
import { Controller, useForm } from 'react-hook-form';
import { Checkbox, FormControlLabel, TextField } from '@mui/material';
import { applySelfCreditReport } from '@/api/creditReport.api';
import { Button } from '@/components/UI/Button';
import { H5 } from '@/components/UI/Typography/Heading';
import { ConfirmModal, ErrorModal, PurchaseModal, RejectModal, TermsModal } from '../Modal';
import usePermissionCheck from '@/hooks/usePermissionCheck';
import { errorHandler } from '@/utils/toastHandler';
import { theme } from '@/theme/Theme';
import { useAppSelector } from '@/redux/store.hook';
import { getAuthSelector } from '@/redux/auth/auth.slice';
import useUserDetail from '@/hooks/useUserDetail';

interface FormStepperProps {
  nextHandler: () => void;
  prevHandler: () => void;
  setData: React.Dispatch<React.SetStateAction<CreditReportResponse | undefined>>;
}

const OwnCompany = ({ nextHandler, prevHandler, setData }: FormStepperProps) => {
  // @ts-ignore
  const { t } = useTranslation();
  const [agree, setAgree] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [showRejectModal, setShowRejectModal] = useState<boolean>(false);
  const [showPurchaseModal, setShowPurchaseModal] = useState<boolean>(false);
  const [showErrorModal, setShowErrorModal] = useState<boolean>(false);
  const [showTermsModal, setShowTermsModal] = useState<boolean>(false);
  const { userData } = useAppSelector(getAuthSelector);
  const quota = useLegacyExternalQuota('prc_credit_report', userData.user_feature?.ESignatureDigitalCertTimes);
  const count = quota.decision.available ?? 0;
  const submitLock = useRef(false);
  const requestKeys = useRef(legacyExternalRequestKeys(newRequestKey));

  const { isMainAccount } = usePermissionCheck();

  const handleAgree = () => {
    setAgree(!agree);
  };

  const { setValue, handleSubmit, control } =
    useForm<Omit<CreditReportApplicationForm, 'companyName, queryType, companyId'>>();

  const onSubmit = handleSubmit(async (data) => {
    if (submitLock.current || !isMainAccount || userData.acc_country !== 'China') return;
    let nativeAttempted = false;
    submitLock.current = true;
    setIsLoading(true);
    setShowConfirmModal(false);
    setShowPurchaseModal(false);
    setShowTermsModal(false);

    try {
      const access = await quota.refresh();
      if (access.state !== 'available') { if (access.state === 'exhausted') setShowPurchaseModal(true); return; }
      nativeAttempted = true;
      const res = (await applySelfCreditReport(data, access.managed ? requestKeys.current.forRequest(data) : undefined)).data;

      if (!quota.isCurrent()) return;
      if (res.status) {
        requestKeys.current.completed();
        setData(res.report);
        nextHandler();
      }
    } catch (error) {
      if (isExternalReconciliation(error) || (nativeAttempted && isExternalOutcomeUncertain(error))) { quota.markReconciliation(isLegacyBalanceReconciliation(error) ? 'legacy_balance' : undefined); return; }
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status && status >= 400 && status < 500) requestKeys.current.rejected();

      setShowErrorModal(true);
    } finally {
      submitLock.current = false;
      setIsLoading(false);
    }
  });

  const modalHanlder = async () => {
    if (!isMainAccount) {
      errorHandler(t('error.permission'));
      return;
    }

    if (userData.acc_country !== 'China') {
      setShowRejectModal(true);
      return;
    }
    const access = await quota.refresh();
    if (access.state === 'exhausted') {
      setShowPurchaseModal(true);
      return;
    } else if (access.state === 'available') {
      setShowTermsModal(true);
      return;
    }
  };

  useEffect(() => {
    if (!userData.acc_coname) return;
    setValue('name', userData.acc_coname);
    if (!isMainAccount) {
      errorHandler(t('error.permission'));
      prevHandler();
      return;
    }

  }, [t, userData.id]);

  useEffect(() => { requestKeys.current.completed(); setShowConfirmModal(false); setShowPurchaseModal(false); setShowTermsModal(false); setAgree(false); }, [userData.id]);

  const modalSwitch = () => {
    setShowTermsModal(false);
    setShowConfirmModal(true);
  };

  return (
    <form onSubmit={onSubmit}>
      <LegacyExternalQuotaNotice decision={quota.decision} retry={quota.refresh} />
      <ConfirmModal unlimited={quota.decision.unlimited} count={count} onSubmit={onSubmit} showModal={showConfirmModal} setShowModal={setShowConfirmModal} />
      <RejectModal showModal={showRejectModal} setShowModal={setShowRejectModal} />
      <PurchaseModal showModal={showPurchaseModal} setShowModal={setShowPurchaseModal} />
      <ErrorModal showModal={showErrorModal} setShowModal={setShowErrorModal} />
      <TermsModal onSubmit={modalSwitch} showModal={showTermsModal} setShowModal={setShowTermsModal} />
      <LoadingPage isLoading={isLoading} />
      <Box>
        <InputWrapper style={{ height: 'auto' }}>
          <Controller
            control={control}
            name={'name'}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                label={t('form.companyName.label')}
                variant="standard"
                value={value}
                sx={{ width: '100%', marginRight: '15px' }}
                onChange={onChange}
                error={!!error}
                helperText={error ? error.message : null}
                autoComplete="off"
                inputProps={{
                  readOnly: true,
                }}
              />
            )}
            rules={{ required: t('required.input', { input: t('form.companyName.label') }) }}
          />
        </InputWrapper>

        <FormControlLabel
          control={
            <Checkbox
              sx={{
                color: '#999999',
                '&.Mui-checked': {
                  color: '#999999',
                },
              }}
              checked={agree}
              onChange={handleAgree}
            />
          }
          label={t('form.agreePlatformUseInfo.label')}
        />
      </Box>

      <Box marginTop={30}>
        <div>{t('pages.eTrust.creditReport.steps.step1.useCR')}</div>
        <H5 marginTop={15} color={theme.colors.primary500}>
          {t('pages.eTrust.creditReport.steps.step1.remainCR', {
            count,
          })}
        </H5>
      </Box>

      <Flex marginTop={50}>
        <Button
          size={'lg'}
          type="button"
          variant={'primary'}
          rounded={false}
          marginRight={40}
          disabled={!agree}
          onClick={modalHanlder}
        >
          {t('buttons.submit')}
        </Button>
        <Button size={'lg'} type="button" variant="primary-invert" marginRight={40} onClick={prevHandler}>
          {t('buttons.back')}
        </Button>
      </Flex>
    </form>
  );
};

export default OwnCompany;
